import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";
import { geoEqualEarth } from "d3-geo";
import api from "../../api";
import ChartInfoPopup from "./ChartInfoPopup";

const geoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const MAP_WIDTH = 800;
const MAP_HEIGHT = 500;
const MAP_SCALE = 160;

const UserPresenceMap = () => {
  const [markers, setMarkers] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [svgBounds, setSvgBounds] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const mapWrapperRef = useRef(null);
  const markerLayerRef = useRef(null);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(
        document.body.classList.contains("dark-mode")
      );
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/auth/user-presence`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const users = response.data.users || [];
        const countryMap = new Map();

        users.forEach((user) => {
          user.active_sessions.forEach((session) => {
            if (
              session.latitude &&
              session.longitude &&
              session.country
            ) {
              const key = session.country;

              if (!countryMap.has(key)) {
                countryMap.set(key, {
                  country: session.country,
                  coordinates: [
                    parseFloat(session.longitude),
                    parseFloat(session.latitude),
                  ],
                  count: 0,
                  visitors: new Set(),
                });
              }

              const entry = countryMap.get(key);
              entry.count += 1;
              entry.visitors.add(user.name);
            }
          });
        });

        setMarkers(
          Array.from(countryMap.values()).map((entry) => ({
            ...entry,
            visitors: Array.from(entry.visitors),
          }))
        );
      } catch (error) {
        console.error(
          "Failed to fetch user presence:",
          error
        );
      }
    };

    fetchPresence();
  }, [token]);

  useLayoutEffect(() => {
    const updateSvgBounds = () => {
      const svg = mapWrapperRef.current?.querySelector("svg");

      if (!svg) {
        setSvgBounds(null);
        return;
      }

      const rect = svg.getBoundingClientRect();
      const wrapperRect =
        mapWrapperRef.current.getBoundingClientRect();

      setSvgBounds({
        top: rect.top,
        left: rect.left,
        relativeTop: rect.top - wrapperRect.top,
        relativeLeft: rect.left - wrapperRect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    updateSvgBounds();

    window.addEventListener("resize", updateSvgBounds);
    window.addEventListener("scroll", updateSvgBounds, true);

    return () => {
      window.removeEventListener("resize", updateSvgBounds);
      window.removeEventListener("scroll", updateSvgBounds, true);
    };
  }, [markers, isDarkMode]);

  const projection = useMemo(
    () =>
      geoEqualEarth()
        .scale(MAP_SCALE)
        .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]),
    []
  );

  const projectedMarkers = useMemo(() => {
    if (!svgBounds) {
      return [];
    }

    return markers
      .map(({ country, coordinates, count }) => {
        const point = projection(coordinates);

        if (!point) {
          return null;
        }

        const [x, y] = point;

        return {
          country,
          count,
          left:
            svgBounds.relativeLeft +
            (x / MAP_WIDTH) * svgBounds.width,
          top:
            svgBounds.relativeTop +
            (y / MAP_HEIGHT) * svgBounds.height,
        };
      })
      .filter(Boolean);
  }, [markers, projection, svgBounds]);

  return (
    <div className="chart-card p-3 shadow-sm rounded mb-4 d-flex flex-column">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h4 className="fw-bold mb-0">
          <span className="border-start border-4 ps-2 ak-text-lg d-flex align-content-center refresh-prompts">
            User Presence
          </span>
        </h4>

        <div className="chart-info-anchor">
          <ChartInfoPopup text="This world map illustrates where users are interacting with the chatbot." />
        </div>
      </div>

      <div
        ref={mapWrapperRef}
        style={{
          height: "300px",
          width: "100%",
          marginTop: "auto",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <ComposableMap
          projectionConfig={{ scale: MAP_SCALE }}
          width={MAP_WIDTH}
          height={MAP_HEIGHT}
          style={{
            width: "100%",
            height: "100%",
            outline: "none",
          }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: isDarkMode
                        ? "#444444"
                        : "#ECEFF1",
                      stroke: isDarkMode
                        ? "#888888"
                        : "#607D8B",
                      strokeWidth: 0.5,
                    },
                    hover: {
                      fill: isDarkMode
                        ? "#555555"
                        : "#CFD8DC",
                      stroke: isDarkMode
                        ? "#BBBBBB"
                        : "#607D8B",
                      strokeWidth: 1,
                    },
                    pressed: {
                      fill: isDarkMode
                        ? "#666666"
                        : "#B0BEC5",
                    },
                  }}
                />
              ))
            }
          </Geographies>
        </ComposableMap>

        <div
          ref={markerLayerRef}
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />

        {svgBounds &&
          markerLayerRef.current &&
          createPortal(
            <>
              {projectedMarkers.map(
                ({ country, count, left, top }) => (
                  <div
                    key={country}
                    onMouseEnter={() =>
                      setActiveTooltip({
                        content: `${country} - ${count} visitor(s)`,
                        left: svgBounds.left + left,
                        top: svgBounds.top + top,
                      })
                    }
                    onMouseLeave={() =>
                      setActiveTooltip(null)
                    }
                    style={{
                      position: "absolute",
                      left: `${left}px`,
                      top: `${top}px`,
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: "#FF5722",
                      border: "none",
                      boxShadow:
                        "0 0 0 1px rgba(0, 0, 0, 0.35)",
                      transform: "translate(-50%, -50%)",
                      pointerEvents: "auto",
                      cursor: "pointer",
                    }}
                  />
                )
              )}
            </>,
            markerLayerRef.current
          )}

        {activeTooltip &&
          createPortal(
            <div
              style={{
                position: "fixed",
                left: `${activeTooltip.left}px`,
                top: `${activeTooltip.top - 14}px`,
                transform: "translate(-50%, -100%)",
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.96)"
                  : "rgba(28, 28, 28, 0.96)",
                color: isDarkMode ? "#1f2937" : "#f5f5f5",
                border: isDarkMode
                  ? "1px solid rgba(255, 255, 255, 0.14)"
                  : "1px solid rgba(31, 41, 55, 0.12)",
                borderRadius: "8px",
                boxShadow: isDarkMode
                  ? "0 10px 28px rgba(0, 0, 0, 0.45)"
                  : "0 10px 24px rgba(15, 23, 42, 0.14)",
                fontSize: "12px",
                padding: "8px 10px",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                zIndex: 1100,
              }}
            >
              {activeTooltip.content}
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default UserPresenceMap;
